/**
 * Master-Sprint P0 (2026-06) — mobile bottom sheet for heatmap detail
 * filters. Renders the SHARED HeatmapChipBar (hideModeRow) against a
 * DRAFT state: nothing fetches while the user edits; "Filter anwenden"
 * commits the draft in ONE patch (single refetch, no double requests).
 */
import React, { useEffect, useState } from "react";
import { X } from "lucide-react";
import { HeatmapChipBar } from "./HeatmapChipBar";

const DETAIL_KEYS = [
  "price_min", "price_max", "area_min", "area_max", "rooms_min",
  "yield_min", "discount_min", "types", "region", "country", "features",
  "makes", "models", "year_min", "year_max", "km_max", "fuels",
  "gearboxes", "body_types", "seller_types", "damage", "price_reduced",
];

export default function HeatmapMobileFilterSheet({
  open,
  onClose,
  vertical,
  mode,
  filters,
  onApply,
  autoOptions,
  immoOptions,
  modelOptions,
  t,
}) {
  const [draft, setDraft] = useState(filters);
  useEffect(() => {
    if (open) setDraft(filters);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);
  if (!open) return null;

  const patchDraft = (p) => setDraft((d) => ({ ...d, ...p }));
  const resetDraft = () =>
    setDraft((d) => {
      const next = { ...d };
      DETAIL_KEYS.forEach((k) => { next[k] = ""; });
      return next;
    });
  const activeCount = DETAIL_KEYS.filter((k) => draft[k]).length;

  return (
    <div className="fixed inset-0 z-[1200] md:hidden" data-testid="heatmap-filter-sheet">
      <div
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        data-testid="heatmap-filter-sheet-backdrop"
        onClick={onClose}
      />
      <div className="absolute inset-x-0 bottom-0 flex max-h-[84dvh] flex-col rounded-t-2xl border-t border-zinc-800 bg-zinc-950 shadow-2xl">
        <div className="flex shrink-0 items-center justify-between px-4 pb-2 pt-3">
          <h2 className="text-sm font-semibold text-zinc-100">
            {t("filtersSheet.title")}
            {activeCount > 0 && (
              <span className="ml-2 rounded-full bg-amber-500/15 px-2 py-0.5 text-[10px] font-bold text-amber-300">
                {activeCount}
              </span>
            )}
          </h2>
          <button
            type="button"
            data-testid="heatmap-filter-sheet-close"
            onClick={onClose}
            className="rounded-full p-1.5 text-zinc-500 transition-colors hover:bg-zinc-800 hover:text-zinc-200"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
        <div className="min-h-0 flex-1 overflow-y-auto px-4 py-2">
          <HeatmapChipBar
            hideModeRow
            vertical={vertical}
            mode={mode}
            filters={draft}
            onVertical={() => {}}
            onMode={() => {}}
            onPatch={patchDraft}
            autoOptions={autoOptions}
            immoOptions={immoOptions}
            modelOptions={modelOptions}
            t={t}
          />
        </div>
        <div
          className="flex shrink-0 items-center gap-2 border-t border-zinc-800/80 px-4 pt-3"
          style={{ paddingBottom: "calc(env(safe-area-inset-bottom, 0px) + 12px)" }}
        >
          <button
            type="button"
            data-testid="heatmap-filter-sheet-reset"
            onClick={resetDraft}
            className="rounded-full border border-zinc-700 px-4 py-2 text-xs font-semibold text-zinc-300 transition-colors hover:border-zinc-500"
          >
            {t("filtersSheet.reset")}
          </button>
          <button
            type="button"
            data-testid="heatmap-filter-sheet-apply"
            onClick={() => {
              onApply(draft);
              onClose();
            }}
            className="flex-1 rounded-full bg-amber-500 px-4 py-2 text-xs font-bold text-zinc-950 transition-colors hover:bg-amber-400"
          >
            {t("filtersSheet.apply")}
          </button>
        </div>
      </div>
    </div>
  );
}
